/**
 * AtlasAudioRuntimeModule — what the shared audio runtime cannot know by itself
 * (2026-09-20): where the ears are, what room they are in, what the machine can
 * afford, and when the world changed. The runtime (lib/audio/runtime.ts) is a
 * singleton that works WITHOUT this module (a UI click at the menu needs no
 * camera); this module only makes it spatially and acoustically true.
 *
 * ONE LISTENER, ONE POLICY. Zones, positional effects and proximity voice used
 * three conventions (the zones never turned; voice was yaw-only at a fixed
 * 1.6 m). They now share this pose:
 *
 *   first person / space   the camera's pose — what you see is where you hear from
 *   third person, a ride,  the camera's ORIENTATION at the player's HEAD / craft —
 *   atmospheric flight     a chase camera 8–170 m behind would otherwise hear
 *                          through the wall behind it and miss what is beside you
 *
 * The camera lives in RENDER space (scene.position = −origin, the FP64 rebase);
 * every emitter in the game is given in ABSOLUTE coordinates. The conversion
 * happens here, once: absolute = render − scene.position.
 *
 * System `AtlasAudioRuntime`, Animation / 49 — after the avatar and the camera
 * are posed, one slot before the zone tick that reads the pose.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { ASSET_STORE_BASE } from '../config.js';
import { audioRuntime, type LoopHandle } from '../lib/audio/runtime.js';
import { PASSING_CRAFT, pickPassingCraft, type CraftKind, type CraftSeen, type CraftSlot } from '../lib/audio/passing_craft.js';
import { liveCraft } from '../lib/audio/craft_feed.js';
import { activeTier } from '../lib/quality_tiers.js';

const HEAD_M = 1.6;

export class AtlasAudioRuntimeModule implements IModule {
  readonly name = 'AtlasAudioRuntime';
  private app: App | null = null;
  private readonly camPos = new THREE.Vector3();
  private readonly camQ = new THREE.Quaternion();
  private physics: { rayBlocked?(ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean | null } | null | undefined;
  private tier = '';
  private captionBox: HTMLDivElement | null = null;
  private offs: Array<() => void> = [];
  // ── other travelers' engines (lib/audio/passing_craft.ts) ──
  private craftAcc = 0;
  private readonly craftSlots: CraftSlot[] = [];
  private readonly craftLast = new Map<string, { x: number; y: number; z: number; vx: number; vy: number; vz: number; t: number }>();
  private readonly craftLoops = new Map<string, LoopHandle>(); // `${slot}:${kind}` → its loop
  private craftDiag: Array<{ id: string; kind: CraftKind; slot: number; dist: number; gain: number; rate: number }> = [];

  init(app: App): void {
    this.app = app;
    const rt = audioRuntime();
    rt.catalogBase = ASSET_STORE_BASE;
    rt.setTier(activeTier());
    this.tier = activeTier();
    // The obstruction probe resolves PhysicsModule lazily: it is a phase-2 module, this one is not.
    rt.setObstructionProbe((ax, ay, az, bx, by, bz) => {
      if (this.physics === undefined) this.physics = (app.getModule('PhysicsModule') as typeof this.physics) ?? undefined;
      return this.physics?.rayBlocked?.(ax, ay, az, bx, by, bz) ?? null;
    });
    rt.setCaptionSink((text, bearing) => this.showCaption(text, bearing));
    // A world left behind takes its delayed sounds with it (thunder up to 10.5 s late, a second footstep, a respawn chime).
    this.offs.push(bus.on('atlas:world-cleared', () => rt.leaveWorld()));
    this.offs.push(bus.on('atlas:quality-changed', () => { const t = activeTier(); if (t !== this.tier) { this.tier = t; rt.setTier(t); } }));
    app.world.addSystem(makeSystem('AtlasAudioRuntime', Phase.Animation, 49, (dt) => this.tick(dt)));
    (window as unknown as { __passingCraft?: () => unknown }).__passingCraft = () => ({ voices: this.craftDiag, held: this.craftSlots.map((c) => c.id?.slice(0, 8) ?? null), tracked: this.craftLast.size });
  }

  private tick(dt: number): void {
    const rt = audioRuntime();
    if (!rt.ctx) return;
    const t = activeTier();
    if (t !== this.tier) { this.tier = t; rt.setTier(t); }
    const app = this.app;
    const cam = app?.renderer.cameras.main as THREE.Camera | undefined;
    if (cam && app) {
      cam.getWorldQuaternion(this.camQ);
      // Third person on foot, a hovercraft, a ship in atmosphere: the ears ride the BODY, the head turns with the camera.
      const bodyEars = atlas.currentWorldId !== null && !atlas.spaceFlight && (atlas.camBack > 0.01 || atlas.hoverRide || atlas.skyFlight);
      if (bodyEars) {
        rt.setListenerPose(atlas.localX, atlas.localY + HEAD_M, atlas.localZ, this.camQ.x, this.camQ.y, this.camQ.z, this.camQ.w);
      } else {
        cam.getWorldPosition(this.camPos);
        const sp = app.world.scene.position; // render → absolute (scene.position = −origin, the FP64 rebase)
        rt.setListenerPose(this.camPos.x - sp.x, this.camPos.y - sp.y, this.camPos.z - sp.z, this.camQ.x, this.camQ.y, this.camQ.z, this.camQ.w);
      }
    }
    const interior = typeof atlas.interiorId === 'string' && atlas.interiorId !== '';
    rt.setEnvironment({
      submerged: atlas.submerged,
      indoors: interior,
      inCave: typeof atlas.caveId === 'string' && atlas.caveId !== '',
      spaceFlight: atlas.spaceFlight,
      aboardShip: interior && (atlas.interiorId as string).startsWith('skyship:'),
    });
    this.passingCraft(dt);
    rt.update(dt);
  }

  /**
   * Another player's hovercraft or ship within earshot gets an engine: the nearest three, positional, pitched by
   * their speed and a Doppler shift. Scanned at 8 Hz (velocity is a finite difference of the interpolated body — a
   * per-frame difference is mostly interpolation noise); the loops' positions are refreshed on the same cadence,
   * which at 45 m/s is a 5 m step at ≥ 10 m — well under what a panner can resolve.
   */
  private passingCraft(dt: number): void {
    this.craftAcc += dt;
    if (this.craftAcc < 0.125) return;
    const span = this.craftAcc;
    this.craftAcc = 0;
    const rt = audioRuntime();
    const now = performance.now();
    const seen: CraftSeen[] = [];
    // Velocity is a finite difference of where the craft is DRAWN, eased (a snapshot landing mid-scan would otherwise
    // read as a lurch in pitch).
    const see = (id: string, kind: CraftKind, x: number, y: number, z: number): void => {
      const last = this.craftLast.get(id);
      let vx = 0, vy = 0, vz = 0;
      if (last && now - last.t < 600) {
        const k = 0.35, ix = (x - last.x) / span, iy = (y - last.y) / span, iz = (z - last.z) / span;
        vx = last.vx + (ix - last.vx) * k; vy = last.vy + (iy - last.vy) * k; vz = last.vz + (iz - last.vz) * k;
      }
      this.craftLast.set(id, { x, y, z, vx, vy, vz, t: now });
      seen.push({ id, kind, x, y, z, vx, vy, vz });
    };
    if (atlas.currentWorldId !== null && !atlas.spaceFlight && !atlas.loadingGate) {
      for (const r of atlas.remotes.values()) {
        const kind: CraftKind | null = r.animState.startsWith('ride:') ? 'hover' : r.animState.startsWith('fly:') ? 'ship' : null;
        if (!kind || !r.mesh) continue;
        const p = r.mesh.position; // a scene child: ABSOLUTE coordinates (the FP64 rebase law)
        see(r.identityHex, kind, p.x, p.y, p.z);
      }
      // NPC craft report themselves (lib/audio/craft_feed.ts): versona journeys, the cargo lane's freighter and escorts.
      for (const c of liveCraft(now)) see(c.id, c.kind, c.x, c.y, c.z);
    }
    if (this.craftLast.size > 64) for (const [id, l] of this.craftLast) if (now - l.t > 5000) this.craftLast.delete(id);
    const pose = rt.listenerPose();
    const voices = pickPassingCraft(seen, pose.x, pose.y, pose.z, this.craftSlots, now / 1000);
    const live = new Set<string>();
    for (const v of voices) {
      const id = `${v.slot}:${v.kind}`;
      live.add(id);
      let loop = this.craftLoops.get(id);
      if (!loop) {
        // A kind whose own loop is not installed borrows its fallback (decided once per slot+kind; a clip landing later is picked up on the next page load).
        const own = PASSING_CRAFT.key[v.kind], fb = PASSING_CRAFT.fallbackKey[v.kind];
        const key = fb && rt.available(own) === false ? fb : own;
        loop = rt.loop(`craft:${v.slot}`, key, { priority: 35, distance: 'caller', occlude: false });
        this.craftLoops.set(id, loop);
      }
      loop.at({ x: v.x, y: v.y, z: v.z });
      loop.set(v.gain, v.rate, 0.2);
    }
    for (const [id, loop] of this.craftLoops) if (!live.has(id)) loop.set(0, 1, 0.35); // idle → the runtime virtualises it after 2 s
    this.craftDiag = voices.map((v) => ({ id: v.id.slice(0, 8), kind: v.kind, slot: v.slot, dist: +v.dist.toFixed(1), gain: +v.gain.toFixed(3), rate: +v.rate.toFixed(3) }));
  }

  // ── sound captions (Options → Sound → "Sound captions") ──
  /** What was heard, and from where — never what it MEANS (the bounty hunter's evidence law applies to every caption). */
  private showCaption(text: string, bearing: string | null): void {
    if (!this.captionBox) {
      const box = document.createElement('div');
      box.id = 'atlas-sound-captions';
      box.setAttribute('aria-live', 'polite');
      box.style.cssText = 'position:fixed;right:18px;bottom:150px;z-index:45;display:flex;flex-direction:column;gap:4px;align-items:flex-end;pointer-events:none;'
        + 'font:13px/1.35 system-ui;color:#e8f2fc;text-shadow:0 1px 2px #000';
      document.body.appendChild(box);
      this.captionBox = box;
    }
    const line = document.createElement('div');
    line.textContent = bearing ? `[ ${text} — ${bearing} ]` : `[ ${text} ]`;
    line.style.cssText = 'background:rgba(6,10,18,0.72);border:1px solid #35506a;border-radius:6px;padding:3px 9px;transition:opacity .4s';
    this.captionBox.appendChild(line);
    while (this.captionBox.childElementCount > 4) this.captionBox.firstElementChild?.remove();
    window.setTimeout(() => { line.style.opacity = '0'; }, 2300);
    window.setTimeout(() => { line.remove(); }, 2800);
  }

  dispose(): void {
    for (const off of this.offs) { try { off(); } catch { /* gone */ } }
    this.offs = [];
    const rt = audioRuntime();
    rt.setObstructionProbe(null);
    rt.setCaptionSink(null);
    this.captionBox?.remove();
    this.captionBox = null;
  }
}
